import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BudgetType } from '../../../../enums/BudgetType';
import { BudgetCategoryService } from '../../../../services/budget-category.service';
import { ToastService } from '../../../../services/toast.service';
import { LoadingService } from '../../../../services/loading.service';
import { AuthorizationService } from '../../../../services/authorization.service';

@Component({
  selector: 'app-new-category',
  templateUrl: './new-category.component.html',
  styleUrl: './new-category.component.scss'
})
export class NewCategoryComponent implements OnInit, OnChanges {

  @Input() categoryId: number | null = null;
  @Input() readonly: boolean = false;
  @Output() retrieveData = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();
  budgetCategoryForm: FormGroup;
  budgetTypes: { label: string, value: BudgetType }[];

  constructor(
    private fb: FormBuilder,
    private _budgetCategoryService: BudgetCategoryService,
    private toastService: ToastService,
    private _loadingService : LoadingService,
    private authService: AuthorizationService
  ) {
    this.budgetCategoryForm = this.fb.group({
      budgetType: [null, Validators.required],
      name: ['', Validators.required],
      active: [true]
    });
    this.budgetTypes = this.getBudgetTypes();
  }
  
  // Verificar si puede guardar (crear o actualizar)
  get canSave(): boolean {
    if (this.categoryId) {
      return this.authService.hasPermission('CONFIG_BUDGETS_UPDATE');
    }
    return this.authService.hasPermission('CONFIG_BUDGETS_CREATE');
  }


  ngOnInit(): void {
    // Asegurar que el formulario esté limpio al inicializar
    if (!this.categoryId) {
      this.resetForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['categoryId']) {
      if (this.categoryId) {
        this.loadCategory(this.categoryId);
      } else {
        // Si no hay categoryId, resetear el formulario para modo creación
        this.resetForm();
      }
    }
  }


  loadCategory(id: number) {
    this._budgetCategoryService.getById(id).subscribe({
      next: (category: any) => {
        // Convertir string del backend a valor numérico del enum
        const budgetTypeValue = this.convertBackendBudgetType(category.data.budgetType);
        
        this.budgetCategoryForm.patchValue({
          budgetType: budgetTypeValue,
          name: category.data.name,
          active: category.data.active
        });
      }
    });
  }

  // Método para convertir string del backend a valor del enum
  private convertBackendBudgetType(backendType: string): BudgetType {
    switch (backendType) {
      case 'INCOME':
        return BudgetType.Income;
      case 'EXPENSE':
        return BudgetType.Expense;
      default:
        return BudgetType.Income; // Valor por defecto
    }
  }

  getBudgetTypes() {
    return [
      { label: 'Ingresos', value: BudgetType.Income },
      { label: 'Gastos', value: BudgetType.Expense }
    ];
  }

  // Método para obtener el título dinámico del modal
  getModalTitle(): string {
    if (this.readonly) {
      return 'Detalles de Categoría';
    }
    return this.categoryId ? 'Editar Categoría de Presupuesto' : 'Nueva Categoría de Presupuesto';
  }
  
  // Método para obtener la descripción del modal
  getModalDescription(): string {
    if (this.readonly) {
      return 'Información de la categoría de presupuesto';
    }
    return this.categoryId 
      ? 'Modifique los datos de la categoría' 
      : 'Complete la información para crear una nueva categoría de presupuesto';
  }

  onSubmit() {
    // Validar permisos antes de guardar
    if (this.categoryId) {
      if (!this.authService.checkPermissionWithFeedback('CONFIG_BUDGETS_UPDATE', 'No tienes permisos para actualizar categorías')) {
        return;
      }
    } else {
      if (!this.authService.checkPermissionWithFeedback('CONFIG_BUDGETS_CREATE', 'No tienes permisos para crear categorías')) {
        return;
      }
    }
    
    this._loadingService.show();
    const newCategory = {
      ...this.budgetCategoryForm.value,
      budgetType: Number(this.budgetCategoryForm.value.budgetType)
    };
    if (this.budgetCategoryForm.valid) {
      if (this.categoryId) {
        // Actualizar categoría existente
        this._budgetCategoryService.update(this.categoryId, newCategory).subscribe({
          next: () => {
            this.handleSuccess('Categoría actualizada correctamente.');
          },
          error: () => {
            this.handleError('Se produjo un error al actualizar la categoría.');
          }
        });
      } else {
        // Crear nueva categoría
        this._budgetCategoryService.insert(newCategory).subscribe({
          next: () => {
            this.handleSuccess('Categoría creada correctamente.');
          },
          error: () => {
            this.handleError('Se produjo un error al crear la categoría.');
          }
        });
      }
    }
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.resetForm();
    this.closeModal.emit();
    this.retrieveData.emit();
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
    this.closeModal.emit();
  }

  private resetForm(): void {
    this.budgetCategoryForm.reset({
      budgetType: null,
      name: '',
      active: true
    });
    this.categoryId = null; // Restablece el `categoryId` si estás trabajando con edición
  }

}
